import axios from 'axios';

// Each service owns its axios instance, matching the rest of the app.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const projectId = localStorage.getItem('currentProjectId');
  if (projectId) config.headers['X-Project-Id'] = projectId;
  return config;
});

/** Which project the person is working in, remembered between visits. */
const CURRENT_KEY = 'currentProjectId';

const projectService = {
  async list() {
    const { data } = await api.get('/projects');
    return data.data.projects;
  },

  async create(name, description) {
    const { data } = await api.post('/projects', { name, description });
    return data.data.project;
  },

  async members(projectId) {
    const { data } = await api.get(`/projects/${projectId}/members`);
    return data.data;
  },

  async invite(projectId, role) {
    const { data } = await api.post(`/projects/${projectId}/invites`, { role });
    return data.data.invite;
  },

  async revokeInvite(projectId, code) {
    const { data } = await api.delete(`/projects/${projectId}/invites/${code}`);
    return data;
  },

  async removeMember(projectId, userId) {
    const { data } = await api.delete(`/projects/${projectId}/members/${userId}`);
    return data;
  },

  /** A project's settings, in the shape the Settings page edits. */
  async settings(projectId) {
    const { data } = await api.get(`/projects/${projectId}/settings`);
    return data.data.settings;
  },

  /**
   * Saves only the fields passed in.
   *
   * Settings edits one box at a time, so sending a whole form back would blank
   * whatever that screen did not happen to show.
   */
  async saveSettings(projectId, changes) {
    const { data } = await api.patch(`/projects/${projectId}/settings`, changes);
    return data.data.settings;
  },

  /** What a code would do, before doing it. */
  async previewJoin(code) {
    const { data } = await api.post('/projects/join/preview', { code });
    return data.data;
  },

  async join(code) {
    const { data } = await api.post('/projects/join', { code });
    return data.data;
  },

  current() {
    try {
      return localStorage.getItem(CURRENT_KEY) || null;
    } catch {
      return null;
    }
  },

  /**
   * Forget which project we were in.
   *
   * Called when the session changes hands. The id outlived sign-out, so the
   * next person to sign in on that browser sent the previous person's project
   * on every request - and since scope is now actually enforced, the server
   * correctly refused all of it with a 403. A new signup, who has no projects
   * at all, saw every screen fail to load.
   */
  clearCurrent() {
    try {
      localStorage.removeItem(CURRENT_KEY);
      sessionStorage.removeItem('currentAgentId');
    } catch {
      // A browser refusing storage has nothing to clear.
    }
  },

  setCurrent(projectId) {
    try {
      // sessionStorage is what the older screens read; both are kept in step so
      // switching project does not leave half the app pointing elsewhere.
      localStorage.setItem(CURRENT_KEY, projectId);
      sessionStorage.setItem('currentAgentId', projectId);
    } catch {
      // A browser refusing storage should not break navigation.
    }
  }
};

export default projectService;
